import type { schemas } from '@gonfalon/openapi/types';
import type { View } from '@gonfalon/views/types';
import { v4 as uuidv4 } from 'uuid';

import { getMigrationFlagSettings } from '../migration-flags/getMigrationFlagSettings';

// Core OpenAPI types for flag creation
export type FeatureFlagBody = schemas['FeatureFlagBody'];
export type FlagTemplateRepV2 = schemas['FlagTemplateRepV2'];
export type Variation = schemas['Variation'];

// Extended type for cross-project flag creation
export type FeatureFlagBodyWithProject = FeatureFlagBody & {
  targetProjectKey?: string;
  viewKeys?: string[]; // View keys to be linked to the flag after creation
};

// Simplified flag creation form data matching CreateFlagForm.tsx
export interface CreateFlagFormData {
  // Core required fields
  name: string;
  key: string;

  // Template selection
  flagTemplate?: string;

  // Variation type - matching form radio options
  variationType: 'boolean' | 'string' | 'number' | 'json';

  // Template-applied fields (not user-editable in simplified form)
  variations?: FormVariation[];
  defaults?: {
    onVariation: number;
    offVariation: number;
  };
  clientSideAvailability?: {
    usingMobileKey: boolean;
    usingEnvironmentId: boolean;
  };
  temporary?: boolean;
  tags?: string[];
  description?: string;

  // Migration-specific fields
  purpose?: 'migration';
  stageCount?: 2 | 4 | 6;
  contextKind?: string; // Required for 6-stage migrations

  // Jira integration
  jiraIssues?: string[];

  // Project selection (when creating flags in different projects)
  project?: string; // Project key

  // Maintainer selection
  maintainers?: Array<{ id: string; type: 'member' | 'team' }>;

  // Views selection
  views?: View[];

  // Prerequisites selection
  prerequisites?: FormPrerequisite[];

  // JSON schema validation (raw editor text, parsed at submit time)
  variationJsonSchema?: string;
}

// Form variation structure
export interface FormVariation {
  name: string;
  value: unknown;
}

// Form prerequisite structure for flag creation
export interface FormPrerequisite {
  _key: string;
  key: string;
  variation: number;
  variationId: string;
}

// Props passed to the renderPrerequisites render prop
export interface PrerequisiteRenderProps {
  value: FormPrerequisite[];
  onChange: (prerequisites: FormPrerequisite[]) => void;
  isDisabled: boolean;
  hideViewFlagLink?: boolean;
}

// Lightweight variation structure
export interface LightweightVariation {
  id: string;
  value: unknown;
  name?: string;
}

// Client-side availability configuration
export interface ClientSideAvailability {
  usingMobileKey: boolean;
  usingEnvironmentId: boolean;
}

// Flag template data structure from API
export interface FlagTemplate {
  name: string;
  key: string;
  description?: string;
  variationType: string;
  variations: FormVariation[];
  defaultVariations: {
    onVariation: number;
    offVariation: number;
  };
  clientSideAvailability: ClientSideAvailability;
  temporary: boolean;
  tags: string[];
}

// Safe default values for simplified flag creation form (independent of templates)
export const createFlagFormDefaults: CreateFlagFormData = {
  name: '',
  key: '',
  variationType: 'boolean',
};

// Get initial form data with safe defaults
export function getInitialFormData(): CreateFlagFormData {
  return {
    name: '',
    key: '',
    description: '',
    variationType: 'boolean',
    flagTemplate: TEMPLATE_KEYS.CUSTOM, // Start with custom template selected
    temporary: true, // Default to temporary flag
    clientSideAvailability: {
      usingMobileKey: true,
      usingEnvironmentId: true,
    },
    variations: [
      { name: 'True', value: true },
      { name: 'False', value: false },
    ],
    defaults: {
      onVariation: 0,
      offVariation: 1,
    },
    tags: [],
    jiraIssues: [],
    maintainers: [],
    views: [],
  };
}

// Template keys available in the system
export const TEMPLATE_KEYS = {
  NONE: 'none',
  CUSTOM: 'custom',
  RELEASE: 'release',
  EXPERIMENT: 'experiment',
  KILL_SWITCH: 'kill-switch',
  MIGRATION_2_STAGE: 'migration-2-stage',
  MIGRATION_4_STAGE: 'migration-4-stage',
  MIGRATION_6_STAGE: 'migration-6-stage',
} as const;

export type TemplateKey = (typeof TEMPLATE_KEYS)[keyof typeof TEMPLATE_KEYS];

// Variation types matching form options
export const VARIATION_TYPES = {
  BOOLEAN: 'boolean',
  STRING: 'string',
  NUMBER: 'number',
  JSON: 'json',
} as const;

// Display labels for variation types (sentence case)
export const VARIATION_TYPE_LABELS = {
  boolean: 'Boolean',
  string: 'String',
  number: 'Number',
  json: 'JSON',
} as const;

export type VariationType = (typeof VARIATION_TYPES)[keyof typeof VARIATION_TYPES];

export function isValidVariationType(value: unknown): value is VariationType {
  return (
    value === VARIATION_TYPES.BOOLEAN ||
    value === VARIATION_TYPES.STRING ||
    value === VARIATION_TYPES.NUMBER ||
    value === VARIATION_TYPES.JSON
  );
}

// Default variations for each variation type (when no template is selected)
export function getDefaultVariationsForType(variationType: VariationType): FormVariation[] {
  switch (variationType) {
    case 'boolean':
      return [
        { name: '', value: true }, // Custom template has empty names
        { name: '', value: false },
      ];
    case 'string':
      return [
        { name: '', value: 'variation 1' },
        { name: '', value: 'variation 2' },
      ];
    case 'number':
      return [
        { name: '1', value: 1 }, // Number flags require actual values with string names
        { name: '0', value: 0 },
      ];
    case 'json':
      return [
        { name: '', value: { enabled: true } },
        { name: '', value: { enabled: false } },
      ];
    default:
      return [
        { name: '', value: true },
        { name: '', value: false },
      ];
  }
}

// Default lightweight variations for each variation type
export function getDefaultLightweightVariationsForType(variationType: VariationType): LightweightVariation[] {
  switch (variationType) {
    case 'boolean':
      return [
        { id: uuidv4(), value: true, name: '' },
        { id: uuidv4(), value: false, name: '' },
      ];
    case 'string':
      return [
        { id: uuidv4(), value: 'variation 1', name: '' },
        { id: uuidv4(), value: 'variation 2', name: '' },
      ];
    case 'number':
      return [
        { id: uuidv4(), value: 0, name: '' },
        { id: uuidv4(), value: 1, name: '' },
      ];
    case 'json':
      return [
        { id: uuidv4(), value: { enabled: true }, name: '' },
        { id: uuidv4(), value: { enabled: false }, name: '' },
      ];
    default:
      return [
        { id: uuidv4(), value: 'variation 1', name: '' },
        { id: uuidv4(), value: 'variation 2', name: '' },
      ];
  }
}

// Get default onVariation/offVariation indices for variation type and template
export function getDefaultVariationIndices(templateKey?: string): {
  onVariation: number;
  offVariation: number;
} {
  // Client-side template-specific defaults (experiments and migrations)
  // Custom, Release, Kill Switch should use flag settings via template system
  switch (templateKey) {
    case TEMPLATE_KEYS.EXPERIMENT:
      return { onVariation: 1, offVariation: 0 }; // Treatment, Control
    case TEMPLATE_KEYS.MIGRATION_2_STAGE:
      return { onVariation: 1, offVariation: 0 }; // Complete, Off
    case TEMPLATE_KEYS.MIGRATION_4_STAGE:
      return { onVariation: 2, offVariation: 0 }; // Live, Off
    case TEMPLATE_KEYS.MIGRATION_6_STAGE:
      return { onVariation: 3, offVariation: 0 }; // Live, Off
    default:
      // Custom, Release, Kill Switch get defaults from template system (flag settings)
      // No template uses standard defaults
      return { onVariation: 0, offVariation: 1 }; // True/Enabled, False/Disabled
  }
}

// Conversion helper to transform form data to OpenAPI FeatureFlagBody
export function transformFormDataToFeatureFlagBody(
  formData: CreateFlagFormData,
  options: { targetProjectKey?: string; includeVariationJsonSchema?: boolean } = {},
): FeatureFlagBodyWithProject {
  const {
    name,
    key,
    variations,
    defaults,
    clientSideAvailability,
    temporary,
    tags,
    description,
    jiraIssues,
    purpose,
    stageCount,
    contextKind,
    maintainers,
    views,
    prerequisites,
    variationJsonSchema,
  } = formData;

  // Get migration settings if this is a migration flag
  const migrationSettings = getMigrationFlagSettings(stageCount, contextKind);

  // Extract maintainer info from maintainers array
  const memberMaintainer = maintainers?.find((m) => m.type === 'member');
  const teamMaintainer = maintainers?.find((m) => m.type === 'team');
  const maintainerId = memberMaintainer?.id;
  const maintainerTeamKey = teamMaintainer?.id;

  // Transform form data to match OpenAPI schema
  const body: FeatureFlagBodyWithProject = {
    name,
    key,
    description: description || '', // Default empty description
    temporary: temporary ?? true, // Default to temporary
    tags: tags || [], // Default empty array
    clientSideAvailability: clientSideAvailability || { usingMobileKey: false, usingEnvironmentId: false },
    targetProjectKey: options.targetProjectKey, // Add target project key if provided

    // Add maintainer field (either maintainerId or maintainerTeamKey, not both)
    ...(maintainerId && { maintainerId }),
    ...(maintainerTeamKey && { maintainerTeamKey }),

    // Add migration fields when present
    ...(purpose && { purpose }),
    ...(migrationSettings && { migrationSettings }),

    // Add viewKeys for flag creation
    ...(views && views.length > 0 && { viewKeys: views.map((view) => view.key) }),
  };

  // Only add variations, defaults, and prerequisites for non-migration flags
  if (purpose !== 'migration') {
    body.variations = variations
      ? variations.map((v) => {
          let processedValue = v.value;

          // For JSON type variations, ensure strings are parsed to objects for consistency
          if (formData.variationType === 'json' && typeof v.value === 'string' && v.value.trim()) {
            try {
              processedValue = JSON.parse(v.value);
            } catch {
              processedValue = v.value;
            }
          }

          return {
            _id: uuidv4(), // Generate UUID for each variation
            name: v.name,
            value: processedValue,
            description: '', //
          };
        })
      : [];
    body.defaults = defaults || { onVariation: 0, offVariation: 1 }; // Default indices
    body.initialPrerequisites = (prerequisites ?? [])
      .filter((p) => p.key && p.variationId)
      .map((p) => ({ key: p.key, variationId: p.variationId }));

    if (
      options.includeVariationJsonSchema &&
      formData.variationType !== 'boolean' &&
      variationJsonSchema &&
      variationJsonSchema.trim()
    ) {
      try {
        body.variationJsonSchema = JSON.parse(variationJsonSchema);
      } catch {
        // Invalid JSON — skip submission of schema rather than fail the whole flag create
      }
    }
  }

  // Add Jira issues as custom properties if provided
  if (jiraIssues && jiraIssues.length > 0) {
    body.customProperties = {
      'jira.issues': {
        name: 'Jira issues',
        value: jiraIssues,
      },
    };
  }

  return body;
}

// Helper to apply template defaults to form data
export function applyTemplateToFormData(formData: CreateFlagFormData, template: FlagTemplate): CreateFlagFormData {
  return {
    ...formData,
    flagTemplate: template.key,
    variationType: template.variationType as VariationType,
    variations: template.variations,
    defaults: template.defaultVariations,
    temporary: template.temporary,
    tags: template.tags,
  };
}
